<template>
  <div class="popup-overlay">
    <div class="edit-details-popup">
      <div class="popup-header">
        <h2>Edit {{ type }} Snippet Details</h2>
      </div>
      <div class="popup-body">
        <!-- You can show a loading message or error if needed -->
        <form @submit.prevent="saveDetails">
          <!-- Name (required) -->
          <div class="form-row">
            <label for="name">Name<span>*</span></label>
            <input id="name" type="text" v-model="form.name" required />
          </div>
          <!-- Public Checkbox -->
          <div class="form-row">
            <label for="public">Public</label>
            <input id="public" type="checkbox" v-model="form.public" />
          </div>
          <!-- Folder 1 -->
          <div class="form-row">
            <label for="folder1">Folder 1</label>
            <input id="folder1" type="text" v-model="form.folder1" />
          </div>
          <!-- Folder 2 (only if Folder 1 has a value) -->
          <div class="form-row" v-if="form.folder1">
            <label for="folder2">Folder 2</label>
            <input id="folder2" type="text" v-model="form.folder2" />
          </div>
          <!-- Folder 3 (only if Folder 2 has a value) -->
          <div class="form-row" v-if="form.folder2">
            <label for="folder3">Folder 3</label>
            <input id="folder3" type="text" v-model="form.folder3" />
          </div>
          <!-- Description (Text area 4 rows high) -->
          <div class="form-row">
            <label for="description">Description</label>
            <textarea id="description" v-model="form.description" rows="4"></textarea>
          </div>
          <!-- Keywords -->
          <div class="form-row">
            <label for="keywords">Keywords</label>
            <input id="keywords" type="text" v-model="form.keywords" />
          </div>
          <!-- Organization -->
          <div class="form-row">
            <label for="organization">Organization</label>
            <input id="organization" type="text" v-model="form.organization" />
          </div>
          <!-- Order -->
          <div class="form-row">
            <label for="order">Order</label>
            <input id="order" type="number" v-model.number="form.order" />
          </div>
          <!-- Version Display -->
          <div class="form-row">
            <label for="version">Version</label>
            <input id="version" type="number" v-model.number="form.version" />
          </div>
          <!-- Buttons -->
          <div class="form-buttons">
            <button type="submit">Save</button>
            <button type="button" @click="closePopup" :disabled="disableClose">Close</button>
            <button type="button" @click="confirmDelete">Delete</button>
            <button type="button" @click="openSaveAsCopy">Save As Copy</button>
          </div>
        </form>

        <!-- Mini popup for Save As Copy -->
        <div class="popup-overlay" v-if="showSaveAsCopy">
          <div class="mini-popup">
            <h3>Save As Copy</h3>
            <p>Please choose a new snippet name:</p>
            <!-- The current name with (copy) appended is pre-filled -->
            <input type="text" v-model="copyName" />
            <div class="form-buttons">
              <button type="button" @click="cancelSaveAsCopy">Cancel</button>
              <!-- The Save button is disabled if the name is empty or if it’s the same as the original -->
              <button type="button" :disabled="!copyName.trim() || copyName.trim() === originalDocData.Name" @click="saveAsCopy">
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { doc, getDoc, updateDoc, deleteDoc, addDoc, collection } from "firebase/firestore";
import { db } from "@/firebaseConfig"; // Path to your Firebase credentials file
import { useDbStore } from '@/stores/dbStore';
import { useAuthStore } from "@/stores/authStore"; // Auth State

export default {
  name: "EditDetailsPopup",
  props: {
    type: {
      type: String,
      required: true,
    },
    selectedCssDocId: {
      type: String,
    },
    selectedHtmlDocId: {
      type: String,
    },
  },
  setup(props, { emit }) {
    const form = ref({
      name: "",
      public: false,
      folder1: "",
      folder2: "",
      folder3: "",
      description: "",
      keywords: "",
      organization: "",
      order: 0,
      version: 0,
    });
    const authStore = useAuthStore();
    const dbStore = useDbStore();
    const loading = ref(false);
    const error = ref(null);
    const activeDocId = ref(null);
    const newDocument = ref(null);
    const showSaveAsCopy = ref(false);
    const copyName = ref("");
    const originalDocData = ref({});
    const disableClose = ref(false);

    const docId = computed(() => (props.type === "CSS" ? props.selectedCssDocId : props.selectedHtmlDocId));

    const userPath = computed(() => {
      let path = `users/${authStore.user.uid}/snippets`; // Default path to user's snippets
      const selectedSnippet = dbStore.snippets.find(snippet => snippet.id === docId.value);
      if (authStore.admin && selectedSnippet?.isPublic) {
        path = "users/public/snippets"; // Admin can save to public snippets
      }
      return path;
    });

    onMounted(() => {
      if (docId.value) {
        activeDocId.value = docId.value;
        loadDocument();
      } else {
        console.error("Document ID is not defined.");
      }
    });

    const loadDocument = async () => {
      loading.value = true;
      const docRef = doc(db, userPath.value, docId.value);
      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          originalDocData.value = data;
          form.value = {
            name: data.name || "",
            public: data.public || false,
            folder1: data.folder1 || "",
            folder2: data.folder2 || "",
            folder3: data.folder3 || "",
            description: data.description || "",
            keywords: data.keywords || "",
            organization: data.organization || "",
            order: data.order || 0,
            version: data.version || 0,
          };
        } else {
          error.value = "Document not found.";
        }
      } catch (err) {
        console.error("Error loading document:", err);
        error.value = "Error loading document.";
      } finally {
        loading.value = false;
      }
    };

    const saveDetails = async () => {
      if (!form.value.name.trim()) {
        alert("Name is required.");
        return;
      }

      const docRef = doc(db, userPath.value, activeDocId.value);
      const updatedData = {
        name: form.value.name,
        public: form.value.public,
        folder1: form.value.folder1,
        folder2: form.value.folder2,
        folder3: form.value.folder3,
        description: form.value.description,
        keywords: form.value.keywords,
        organization: form.value.organization,
        order: form.value.order,
        version: form.value.version,
      };

      try {
        await updateDoc(docRef, updatedData);
        if (newDocument.value) {
          emit("DocumentCreated", newDocument.value);
        }
        emit("close");
      } catch (err) {
        console.error("Error saving document:", err);
        alert("Error saving document.");
      }
    };

    const saveAsCopy = async () => {
      const newName = copyName.value.trim();
      if (newName === originalDocData.value.name) {
        alert("Please choose a different name than the original.");
        return;
      }

      const newDocData = {
        ...originalDocData.value,
        ...form.value,
        name: newName,
        version: 1,
      };

      try {
        const newDocRef = await addDoc(collection(db, userPath.value), newDocData);
        newDocument.value = { id: newDocRef.id, ...newDocData };
        activeDocId.value = newDocRef.id;
        form.value.version = 1;
        form.value.name = newName;
        showSaveAsCopy.value = false;
        disableClose.value = true;
      } catch (err) {
        console.error("Error saving as copy:", err);
        alert("Error saving as copy.");
      }
    };

    const openSaveAsCopy = () => {
      copyName.value = form.value.name + " (copy)";
      showSaveAsCopy.value = true;
    };

    const cancelSaveAsCopy = () => {
      showSaveAsCopy.value = false;
      copyName.value = "";
    };

    const confirmDelete = () => {
      if (confirm("Are you sure you want to delete this document?")) {
        deleteDocument();
      }
    };

    const deleteDocument = async () => {
      const docRef = doc(db, userPath.value, docId.value);
      try {
        await deleteDoc(docRef);
        emit("refreshData");
        emit("close");
      } catch (err) {
        console.error("Error deleting document:", err);
        alert("Error deleting document.");
      }
    };

    const closePopup = () => {
      emit("close");
    };

    return {
      form,
      loading,
      error,
      activeDocId,
      newDocument,
      showSaveAsCopy,
      copyName,
      originalDocData,
      disableClose,
      docId,
      userPath,
      loadDocument,
      saveDetails,
      saveAsCopy,
      openSaveAsCopy,
      cancelSaveAsCopy,
      confirmDelete,
      deleteDocument,
      closePopup,
    };
  },
};
</script>

<style scoped>
.mini-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #1e1e1e;
  color: #c5c6c7;
  padding: 20px;
  border-radius: 8px;
  width: 300px;
  z-index: 1050;
}

.edit-details-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #1e1e1e;
  color: #c5c6c7;
  padding: 20px;
  border-radius: 8px;
  width: 500px;
  z-index: 1000;
}

.popup-header {
  border-bottom: 1px solid #333;
  margin-bottom: 15px;
}

.popup-header h2 {
  margin: 0;
  font-size: 1.5em;
}

.popup-body {
  max-height: 80vh;
  overflow-y: auto;
}

.form-row {
  display: flex;
  margin-bottom: 10px;
  align-items: center;
}

.form-row label {
  width: 20%;
  text-align: right;
  font-weight: bold;
  padding-right: 10px;
  font-size: 0.9em;
}

.form-row label span {
  color: red;
}

.form-row input[type="text"],
.form-row textarea {
  flex: 1;
  background: #2e2e2e;
  border: 1px solid #444;
  color: #fff;
  padding: 5px;
  border-radius: 4px;
}

.form-row input[type="number"] {
  background: #2e2e2e;
  border: 1px solid #444;
  color: #fff;
  padding: 5px;
  border-radius: 4px;
  width: 60px; /* fits roughly 4 digits */
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
}

.form-buttons button {
  background: #3a3a3a;
  border: none;
  padding: 8px 12px;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
}

.form-buttons button:hover {
  background: #505050;
}

.version-info {
  text-align: right;
  font-size: 0.8em;
  margin-top: 10px;
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>